import {useChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {copyText} from '@github-ui/copy-to-clipboard'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {CheckIcon, UnlockIcon} from '@primer/octicons-react'
import {Button, Dialog, FormControl, Spinner, Stack, Text, TextInput} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useState} from 'react'

import {CopilotChatService} from '../../utils/copilot-chat-service'
import {useChatManager} from '../../utils/CopilotChatManagerContext'
import styles from './ConversationSharingDialog.module.css'

interface ConversationSharingDialogProps {
  closeDialog: () => void
  threadId?: string
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

export const ConversationSharingDialog: React.FC<ConversationSharingDialogProps> = ({
  closeDialog,
  threadId,
  returnFocusRef,
}) => {
  const state = useChatState()
  const manager = useChatManager()

  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSharedSuccess, setShowSharedSuccess] = useState(false)
  const [showConfirmationText, setShowConfirmationText] = useState(false)
  const {selectedThreadID, ssoOrganizations, apiUrl, threads} = state
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const selectedThread = threads.get(threadId || selectedThreadID!)

  const [sharedThreadId, setSharedThreadId] = useState(() => {
    return selectedThread?.sharedID || ''
  })

  const [sharedMessageId, setSharedMessageId] = useState(() => {
    return selectedThread?.sharedMessageID ? selectedThread.sharedMessageID : ''
  })

  const origin = ssrSafeLocation.origin
  const sharedLink = `${origin}/copilot/share/${sharedThreadId}`

  // Reset copied state after delay
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 1500)
      return () => clearTimeout(timer)
    }
  }, [copied])

  // Reset shared success state after delay
  useEffect(() => {
    if (showSharedSuccess) {
      const timer = setTimeout(() => setShowSharedSuccess(false), 1500)
      return () => clearTimeout(timer)
    }
  }, [showSharedSuccess])

  const copySharedLink = useCallback(async () => {
    try {
      await copyText(sharedLink)
      setCopied(true)
      sendEvent('dotcom_chat.activate', {target: 'COPILOT_COPY_SHARED_CONVERSATION_LINK', mode: 'immersive'})
    } catch {
      // Handle error silently
    }
  }, [sharedLink])

  // Update sharedID in dialog on thread navigations
  useEffect(() => {
    const id = threadId || selectedThreadID
    if (id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSharedThreadId(selectedThread?.sharedID || '')
    }
  }, [selectedThread?.sharedID, selectedThreadID, threadId])

  const handleCreateLink = useCallback(async () => {
    setLoading(true)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = threadId || selectedThreadID!

    const chatService = new CopilotChatService(apiUrl, ssoOrganizations)

    // If we are trying to share the selected thread then we can use the last message ID
    // to share the active subthread; if copilotShareActiveSubthread flag is enabled.
    // Otherwise, we need to use just the thread ID.
    const res = await chatService.shareThread(
      id,
      copilotFeatureFlags.copilotShareActiveSubthread && id === selectedThreadID ? manager.FindLastMessageID() : '',
    )

    if (res.ok) {
      setLoading(false)
      const {sharedAt, sharedID, sharedMessageID} = res.payload
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setSharedThreadId(sharedID!)
      setShowSharedSuccess(true)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setSharedMessageId(sharedMessageID!)

      manager.dispatch({
        type: 'SHARED_THREAD_UPDATED',
        thread: {id, sharedID, sharedAt, sharedMessageID},
      })

      sendEvent('dotcom_chat.activate', {
        target: 'COPILOT_CREATE_SHARED_CONVERSATION',
        mode: 'immersive',
        originalThreadId: id,
        sharedThreadId: sharedID,
      })
    } else {
      setLoading(false)
    }
  }, [apiUrl, manager, selectedThreadID, ssoOrganizations, threadId])

  const handleUnshareClick = useCallback(async () => {
    // Show confirmation text on the first click
    if (!showConfirmationText) {
      setShowConfirmationText(true)
      return
    }

    setShowConfirmationText(false)
    setLoading(true)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = threadId || selectedThreadID!
    const previousSharedID = sharedThreadId

    const chatService = new CopilotChatService(apiUrl, ssoOrganizations)
    const res = await chatService.unshareThread(id)

    if (res.ok) {
      setLoading(false)
      setSharedMessageId('')

      const {sharedID, sharedAt, sharedMessageID} = res.payload

      manager.dispatch({
        type: 'SHARED_THREAD_UPDATED',
        thread: {id, sharedID, sharedAt, sharedMessageID},
      })

      sendEvent('dotcom_chat.activate', {
        target: 'COPILOT_UNSHARE_CONVERSATION',
        mode: 'immersive',
        originalThreadId: id,
        sharedThreadId: previousSharedID,
      })
    } else {
      setLoading(false)
    }
  }, [apiUrl, manager, selectedThreadID, showConfirmationText, ssoOrganizations, threadId, sharedThreadId])

  const shareButton = () => {
    if (sharedThreadId) return

    return (
      <Button variant="primary" disabled={loading} onClick={handleCreateLink}>
        Share
      </Button>
    )
  }

  const copyButton = () => {
    if (!sharedThreadId) return

    return (
      <Button variant="primary" autoFocus onClick={copySharedLink}>
        Copy link
      </Button>
    )
  }

  const unshareButton = () => {
    if (!sharedThreadId) return

    return (
      <Button variant="danger" disabled={loading} onClick={handleUnshareClick}>
        Unshare
      </Button>
    )
  }

  const cancelButton = () => {
    if (!sharedThreadId || !showConfirmationText) return

    return (
      <Button variant="default" onClick={() => setShowConfirmationText(false)} className={styles.cancelButton}>
        Cancel
      </Button>
    )
  }

  const getStateDisplay = () => {
    if (loading) {
      return (
        <div className={styles.quickFadeUp}>
          <Spinner size="small" className={styles.spinner} />
          {sharedThreadId ? 'Unsharing chat...' : 'Sharing chat...'}
        </div>
      )
    }

    if (showConfirmationText) {
      return (
        <div className={styles.quickFadeUp}>
          <Text size="medium" className={styles.warningText}>
            Confirm unsharing this chat?
          </Text>
        </div>
      )
    }

    if (copied) {
      return (
        <div className={styles.quickFadeUp}>
          <CheckIcon className={styles.successIcon} />
          <Text size="medium" className={styles.success}>
            Copied to clipboard
          </Text>
        </div>
      )
    }

    if (showSharedSuccess) {
      return (
        <div className={styles.quickFadeUp}>
          <CheckIcon className={styles.successIcon} />
          <Text size="medium" className={styles.success}>
            Chat shared
          </Text>
        </div>
      )
    }

    if (sharedThreadId) {
      return (
        <div>
          <UnlockIcon className={styles.lockIcon} />
          <Text size="medium" className={styles.mutedText}>
            Visible to anyone with the link
          </Text>
        </div>
      )
    }
    // return empty div to ensure proper spacing if no state text is displayed
    return <div />
  }

  return (
    <Dialog
      title={sharedThreadId ? 'Chat shared' : 'Share chat'}
      onClose={closeDialog}
      width="large"
      returnFocusRef={returnFocusRef}
    >
      <div className={styles.container}>
        <Stack direction="vertical" gap="condensed">
          <Stack direction="horizontal" padding="none" align="center" justify="start" gap="condensed">
            {sharedMessageId ? (
              <span>
                <span className={styles.snapshotText}>
                  {`This shared chat is a snapshot, accessible to anyone with the link. It will not update with new messages.`}
                </span>
              </span>
            ) : sharedThreadId ? (
              <span>
                This chat and future messages are visible to anyone with the link. If private repository content is
                included, repository access is required to view.
              </span>
            ) : (
              <span>
                When shared, this chat and future messages will be visible to anyone with the link. If private
                repository content is included, repository access is required to view.
              </span>
            )}
          </Stack>

          {sharedThreadId && (
            <div className={styles.innerContainer}>
              <div className={styles.sharedLinkContainer}>
                <FormControl id="share-link" className={styles.textInputContainer}>
                  <FormControl.Label>Share link</FormControl.Label>
                  <TextInput size="large" readOnly value={sharedThreadId && sharedLink} className={styles.textInput} />
                </FormControl>
              </div>
            </div>
          )}
        </Stack>

        <Stack
          direction="horizontal"
          gap="condensed"
          align="center"
          justify="space-between"
          className={styles.buttonStack}
        >
          <div role="status">{getStateDisplay()}</div>
          <Stack direction="horizontal" gap="condensed" align="center" justify="end">
            {shareButton()}
            {cancelButton()}
            {unshareButton()}
            {copyButton()}
          </Stack>
        </Stack>
      </div>
    </Dialog>
  )
}
